import { create } from "zustand";

// Live remote-access sessions shown as workspace tabs (phase 2). The "Vault" home view is the
// implicit tab with key "vault"; each SSH session (or a web-only placeholder) is another tab.
// RDP is NOT a tab — it launches a native OS client in its own window. The native session
// lifecycle lives in the pane component (Terminal); this store only tracks tabs + the active one.

// "brokered-ssh"/"brokered-rdp" are the web-native Lane B (jump server) panes: the browser opens a
// WebSocket to the broker (clientless RDP canvas / SSH terminal), no desktop app. On the desktop
// build the native "ssh"/"rdp" kinds are used instead; on web, launching a target now opens a
// brokered pane rather than the old download-the-app "placeholder".
export type SessionKind = "ssh" | "rdp" | "placeholder" | "brokered-ssh" | "brokered-rdp" | "brokered-web";

/** Ad-hoc quick-connect parameters (no saved item). Password is transient — never persisted. */
export interface AdhocSshParams {
  host: string;
  port: number;
  username: string;
  password: string;
}
export interface AdhocRdpParams {
  host: string;
  port: number;
  domain: string;
  username: string;
  password: string;
}

export type Launch =
  // `item` is shared by SSH and embedded-RDP saved-item launches; the tab `kind` picks the pane.
  | { mode: "item"; itemId: string }
  | { mode: "adhoc-ssh"; params: AdhocSshParams }
  | { mode: "adhoc-rdp"; params: AdhocRdpParams }
  // Launch from an already-decrypted field map (offline / mid-session-without-internet, §15).
  // `itemId` is optional and only used for a best-effort audit.
  | { mode: "ssh-fields"; fields: Record<string, string>; itemId?: string }
  | { mode: "rdp-fields"; fields: Record<string, string>; itemId?: string };

export interface SessionTab {
  key: string;
  kind: SessionKind;
  title: string;
  launch?: Launch; // absent for placeholder tabs
  itemType?: "ssh" | "rdp"; // for placeholder: which session type was attempted
  connectorId?: string; // brokered web sessions: route through this outbound connector (item override)
}

export const VAULT_TAB = "vault";

interface SessionsState {
  tabs: SessionTab[];
  activeKey: string; // VAULT_TAB or a tab.key
  open: (tab: Omit<SessionTab, "key">) => string;
  close: (key: string) => void;
  setActive: (key: string) => void;
  closeAll: () => void;
}

// Live xterm panes are hidden (display:none) when inactive, which zeroes their measured size;
// nudge a resize on activation so the fit addon re-measures once visible.
function nudgeResize() {
  requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
}

export const useSessions = create<SessionsState>((set) => ({
  tabs: [],
  activeKey: VAULT_TAB,

  open: (tab) => {
    const key = crypto.randomUUID();
    set((s) => ({ tabs: [...s.tabs, { ...tab, key }], activeKey: key }));
    nudgeResize();
    return key;
  },

  close: (key) =>
    set((s) => {
      const tabs = s.tabs.filter((t) => t.key !== key);
      // If the closed tab was active, fall back to the last remaining session, else the vault.
      const activeKey = s.activeKey === key ? (tabs.length ? tabs[tabs.length - 1].key : VAULT_TAB) : s.activeKey;
      if (activeKey !== s.activeKey) nudgeResize();
      return { tabs, activeKey };
    }),

  setActive: (key) => {
    set({ activeKey: key });
    nudgeResize();
  },

  closeAll: () => set({ tabs: [], activeKey: VAULT_TAB }),
}));
